import { _ } from "libs";
import React, { ReactNode, MouseEvent, useState, useEffect, useLayoutEffect } from "react";
import { Club, Court, SlotsData, OpeningHour, Slot, Player, SlotType } from "../../types";
import { find, propEq } from "ramda";
import dragscroll from "dragscroll";
import * as moment from "moment-timezone";
import { extendMoment } from "moment-range";
import { ReservationState } from "../../reducers/reservation";
import Avatar from "../avatar";
import cx from "classnames";
import Loader from "../loader";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { loggedIn } from "../../actions";
import { postWidgetMessage } from "../../libs/widgetMessaging";

const { range } = extendMoment(moment);
const delta = 15;

function createSkip(duration: number, offset = -1): number {
	return duration / delta + offset;
}

export type TimelineProps = {
	club: Club;
	slots: SlotsData;
	timestamp: number;
	getReservationContext: Function;
	clearReservationContext: Function;
	reservation: ReservationState;
	showUnavailability: Function;
	widgetMode?: boolean;
};

function gcScrollTo(element, to = 0, duration = 1000) {
	if (!element) {
		return;
	}

	const start = element.scrollLeft;
	const change = to - start;
	const increment = 20;
	let currentTime = 0;

	const easeInOutQuad = (t: number, b: number, c: number, d: number) => {
		t /= d / 2;
		if (t < 1) {
			return (c / 2) * t * t + b;
		}
		t--;
		return (-c / 2) * (t * (t - 2) - 1) + b;
	};

	const animateScroll = () => {
		currentTime += increment;
		const val = easeInOutQuad(currentTime, start, change, duration);
		element.scrollLeft = val;
		if (currentTime < duration) {
			setTimeout(animateScroll, increment);
		}
	};

	animateScroll();
}

function scrollSelectedSlotToViewport(slotElement) {
	const calendar = document.querySelector(".calendarScrollable");
	let slotAreaWidth = 160;
	let wizardWidth = 360;
	let selectedSlotAreaOffsetRight =
		calendar.clientWidth - (slotElement.offsetLeft - calendar.scrollLeft) - slotAreaWidth;
	if (selectedSlotAreaOffsetRight < wizardWidth - 40) {
		let offset = wizardWidth + 40 - selectedSlotAreaOffsetRight;
		gcScrollTo(calendar, calendar.scrollLeft + offset, 300);
	}
}

function getDayOfWeek(timestamp: number, timeZone: string): number {
	return (
		moment
			.unix(timestamp)
			.tz(timeZone)
			.day() + 1
	);
}
//green slots
const createHandler = (
	court: Court,
	currentSlot: Slot,
	onClick: Function,
	click$: Subject<ClickStreamArgs>
): ((e: MouseEvent<HTMLElement>) => void) | null => {
	return currentSlot.slotType === "available"
		? (e: MouseEvent<HTMLElement>) => {
				scrollSelectedSlotToViewport(e.target);
				const durations = currentSlot.slotContext["available"].durations;
				const args = {
					court,
					duration: durations[0],
					startDateTime: currentSlot.startDateTime,
					slot: currentSlot,
				};
				onClick({ ...args, preloadOnly: true });
				click$.next(args);
		  }
		: null;
};

//yellow slots
const createSelectableHandler = (
	court: Court,
	currentSlot: Slot,
	duration: number,
	onClick: Function,
	click$: Subject<ClickStreamArgs>
): ((e: MouseEvent<HTMLElement>) => void) | null => {
	return currentSlot.slotType === "available"
		? (e: MouseEvent<HTMLElement>) => {
				scrollSelectedSlotToViewport(e.target);
				const args = {
					court,
					duration,
				};
				onClick({ ...args, preloadOnly: true });
				click$.next(args);
		  }
		: null;
};

const createUnavailableHandler = (
	court: Court,
	currentSlot: Slot,
	onClick: Function
): ((e: MouseEvent<HTMLElement>) => void) | null => {
	return currentSlot.slotType === SlotType.NotAvailable ||
		currentSlot.slotType === SlotType.Booked ||
		currentSlot.slotType === SlotType.Blocked
		? (e: MouseEvent<HTMLElement>) => {
				scrollSelectedSlotToViewport(e.target);
				onClick({
					court,
					slot: currentSlot,
				});
		  }
		: null;
};

const createHoursIterator = (startOfDay: moment.Moment, opening: number, closing: number): Iterable<moment.Moment> => {
	const timeRange = range(startOfDay.clone().add(opening, "seconds"), startOfDay.clone().add(closing, "seconds"));
	return timeRange.by("minute", { step: delta, excludeEnd: true });
};

//get amount of delta min slots that are in slot duration
const getSlotUnitLength = (slot: Slot, unitLength: number = 30): number => {
	return (((slot.endDateTime - slot.startDateTime) / (unitLength * 60)) * unitLength) / delta;
};

const createSelectableSlots = (selectedSlot: Slot, closingTime: number): Map<string, Slot> => {
	const selectableSlots = new Map();
	let previousSlot = null;
	let previousDuration = null;

	//@TODO GF-88 selectedSlot is undefined
	if (selectedSlot === undefined || selectedSlot === null) {
		return selectableSlots;
	}

	for (let i in selectedSlot.slotContext.available.durations) {
		const duration = selectedSlot.slotContext.available.durations[i];

		let startDateTime: number;
		let endDateTime: number;

		if (previousSlot === null) {
			startDateTime = selectedSlot.startDateTime;
			endDateTime = startDateTime + duration * 60;
		} else {
			startDateTime = previousSlot.endDateTime;
			endDateTime = startDateTime + (duration - previousDuration) * 60;
		}

		if (endDateTime <= closingTime) {
			const slot = {
				startDateTime,
				endDateTime,
				slotType: "available",
			} as Slot;

			selectableSlots.set(startDateTime.toString(), slot);
			previousSlot = slot;
			previousDuration = duration;
		}
	}

	return selectableSlots;
};

function getFirstAvailableSlot(slots: Slot[]): Slot {
	for (let slot of slots) {
		if (slot.slotType === "available") {
			return slot;
		}
	}

	return null;
}

function getLastAvailableSlot(slots: Slot[]): Slot {
	for (let i = slots.length - 1; i >= 0; i--) {
		const slot = slots[i];
		if (slot.slotType === "available") {
			return slot;
		}
	}

	return null;
}

const createSelectableRow = (
	hasSelectedSlot: boolean,
	startOfDay: moment.Moment,
	startOfCalendar: moment.Moment,
	endOfCalendar: moment.Moment,
	court: Court,
	slotsByTime: SlotsMap,
	slotsArray: Slot[],
	onClick: Function,
	reservation: ReservationState,
	bookingInterval: number,
	clearReservationContext: Function,
	showUnavailability: Function,
	timezone: string,
	click$: Subject<ClickStreamArgs>
): ReactNode[] => {
	const els = [];

	const firstSlot = slotsArray[0];
	const firstAvailableSlot = getFirstAvailableSlot(slotsArray);
	let firstAvailableSlotStart = 0;
	if (firstAvailableSlot) {
		firstAvailableSlotStart = firstAvailableSlot.startDateTime;
	}

	const lastSlot = slotsArray[slotsArray.length - 1];
	const lastAvailableSlot = getLastAvailableSlot(slotsArray);
	let lastAvailableSlotEnd = 0;
	if (lastAvailableSlot) {
		lastAvailableSlotEnd = lastAvailableSlot.endDateTime;
	}

	const slots = slotsByTime.get(court.courtId);

	const closingTime = endOfCalendar.unix();

	if (!firstSlot) {
		//handling the case when there is no first slot at all
		const durationInMins = (endOfCalendar.unix() - startOfCalendar.unix()) / 60;
		els.push(
			<div
				className="slot notAvailable"
				style={{ width: `${durationInMins * 1.333333333}px` }}
				data-type="notAvailable"
				data-start-time=""
				data-end-time=""
				data-duration={durationInMins}
				key={moment.unix()}
			/>
		);
		return els;
	}

	const gap = firstSlot.startDateTime - startOfCalendar.unix();

	if (gap > 0) {
		let durationInMins = gap / 60;
		els.push(
			<div
				className="slot notAvailable"
				style={{ width: `${durationInMins * 1.333333333}px` }}
				data-type="notAvailable"
				data-start-time=""
				data-end-time=""
				data-duration={durationInMins}
				key={moment.unix()}
			/>
		);
	}

	let selectedSlot = null as Slot;

	let selectableSlots = new Map();
	if (hasSelectedSlot) {
		selectedSlot = reservation.slots[0] as Slot;
		selectableSlots = createSelectableSlots(selectedSlot, closingTime);
	}

	let currentSlot = null;
	let skip = 0;
	let selectableIndex = 0;

	const iteratorStart = firstSlot.startDateTime - startOfDay.unix();
	const iteratorEnd = lastSlot.endDateTime - startOfDay.unix();

	const iterator = createHoursIterator(startOfDay, iteratorStart, iteratorEnd);

	for (const time of iterator) {
		const unix = time.unix();

		if (Math.abs(skip) > 0) {
			skip = skip - 1;
			continue;
		}

		if (slots.has(unix.toString()) || selectableSlots.has(unix.toString())) {
			let selected = false;
			let selectable = false;
			let selectedClass = false;
			let booked = false;
			let ownerType = null;
			let handler = null;

			let searchSelectableSlots = true;
			if (slots.has(unix.toString())) {
				currentSlot = slots.get(unix.toString());

				if (currentSlot.slotType === SlotType.NotAvailable) {
					searchSelectableSlots = false;
				}

				if (currentSlot.slotType === SlotType.Booked) {
					booked = true;
					ownerType = currentSlot.slotContext.booking.owner;
					searchSelectableSlots = false;
				}

				if (currentSlot.slotType === SlotType.Blocked) {
					searchSelectableSlots = false;
				}
			}

			if (searchSelectableSlots && selectableSlots.has(unix.toString())) {
				currentSlot = selectableSlots.get(unix.toString());

				if (currentSlot.startDateTime === selectedSlot.startDateTime) {
					selected = true;
					selectedClass = true;
				} else {
					selectable = true;
				}
			}

			const slotLength = getSlotUnitLength(currentSlot, bookingInterval);
			skip = slotLength - 1;

			let durationInMins = (currentSlot.endDateTime - currentSlot.startDateTime) / 60;

			if (selected) {
				durationInMins = selectedSlot.slotContext.available.durations[0];
				skip = createSkip(durationInMins);
				if (reservation.params.duration === durationInMins) {
					handler = clearReservationContext;
				} else {
					handler = createSelectableHandler(court, currentSlot, durationInMins, onClick, click$);
				}
			} else if (selectable) {
				selectableIndex = selectableIndex + 1;
				const currentDuration = selectedSlot.slotContext.available.durations[selectableIndex];
				const previousDuration = selectedSlot.slotContext.available.durations[selectableIndex - 1];
				durationInMins = currentDuration - previousDuration;

				skip = createSkip(durationInMins);

				let newDuration = currentDuration;
				if (reservation.params.duration === currentDuration) {
					newDuration = previousDuration;
				}

				if (reservation.params.duration >= currentDuration) {
					selectedClass = true;
				}

				handler = createSelectableHandler(court, currentSlot, newDuration, onClick, click$);
			} else {
				if (
					currentSlot.slotType === SlotType.NotAvailable ||
					currentSlot.slotType === SlotType.Booked ||
					currentSlot.slotType === SlotType.Blocked
				) {
					handler = createUnavailableHandler(court, currentSlot, showUnavailability);
				} else {
					handler = createHandler(court, currentSlot, onClick, click$);
				}
			}

			const className = cx(
				"slot",
				{
					selected: selectedClass,
					selectable: selectable && !selectedClass,
					championship:
						currentSlot.slotContext && currentSlot.slotContext.booking
							? !!currentSlot.slotContext.booking.championshipId
							: false,
				},
				currentSlot.slotType,
				booked,
				ownerType
			);

			// TODO @BAZO we display user's in reservations if private club, not only in own reservation / partner in reservations
			const partners: Player[] = [];
			const playerIds: string[] = [];
			let owner: Player;

			if (
				currentSlot.slotContext &&
				currentSlot.slotContext.booking &&
				currentSlot.slotContext.booking.players &&
				currentSlot.slotContext.booking.players.length > 0
			) {
				currentSlot.slotContext.booking.players.forEach((player: Player) => {
					if (player.playerId != currentSlot.slotContext.booking.ownerId) {
						partners.push(player);
						playerIds.push(player.playerId);
					} else {
						owner = player;
					}
				});
			}

			//if reservation made by club, an owner is not always konwn
			if (
				currentSlot.slotContext &&
				currentSlot.slotContext.booking &&
				currentSlot.slotContext.booking.owner === "club" &&
				!owner
			) {
				owner = partners.splice(0, 1)[0];
			}

			els.push(
				<div
					className={className}
					style={{ width: `${durationInMins * 1.333333333}px` }}
					data-type={currentSlot.slotType}
					data-start-time={currentSlot.startDateTime}
					data-end-time={currentSlot.endDateTime}
					data-duration={durationInMins}
					data-date={moment
						.unix(currentSlot.startDateTime)
						.tz(timezone)
						.format("H:mm:ss")}
					title={moment
						.unix(currentSlot.startDateTime)
						.tz(timezone)
						.format("H:mm")}
					key={time.unix()}
					onClick={handler}
				>
					{booked && currentSlot.slotContext.booking.players.length > 0 && (
						<div className="players" data-number-of-players={owner ? partners.length + 1 : partners.length}>
							{owner && <Avatar photoUrl={owner && owner.photoUrl ? owner.photoUrl : ""} size="thumb4" />}
							{partners.map((player, key) => (
								<Avatar key={key} photoUrl={player.photoUrl} size="thumb4" />
							))}
							{currentSlot.slotContext &&
							currentSlot.slotContext.booking &&
							currentSlot.slotContext.booking.championshipId && <div className="championship icon" />}
						</div>
					)}

					{currentSlot.slotContext &&
					 currentSlot.slotContext.booking &&
					 currentSlot.slotContext.booking.championshipId &&
					 currentSlot.slotContext.booking.players?.length === 0 &&
					 	<div className="championship icon" />}
				</div>
			);
		} else {
			let type = "unknown";
			let duration = bookingInterval;
			let handler;

			if (unix < firstSlot.startDateTime) {
				duration = (firstSlot.startDateTime - unix) / 60;
			}

			skip = createSkip(duration);

			if (unix >= firstAvailableSlotStart && unix < lastAvailableSlotEnd) {
				type = "available";
			} else {
				handler = clearReservationContext;
			}

			els.push(
				<div
					className="slot"
					style={{ width: `${duration * 1.333333333}px` }}
					data-type={type}
					data-start-time={unix}
					data-date={moment
						.unix(unix)
						.tz(timezone)
						.format("H:mm:ss")}
					data-duration={duration}
					title={moment
						.unix(unix)
						.tz(timezone)
						.format("H:mm")}
					key={unix}
					onClick={handler}
				/>
			);
		}
	}

	return els;
};

type SlotsMap = Map<string, Map<string, Slot>>;

const orderSlotsByTime = (slots: SlotsData): SlotsMap => {
	let courtsMap = new Map();
	for (const court of slots.courts) {
		let slotsMap = new Map();

		court.slots.forEach((slot: Slot) => {
			slotsMap.set(slot.startDateTime.toString(), slot);
		});

		courtsMap.set(court.courtId, slotsMap);
	}

	return courtsMap;
};

function getCourtSlots(slotsData: SlotsData, courtId: string): Slot[] {
	for (let court of slotsData.courts) {
		if (court.courtId === courtId) {
			return court.slots;
		}
	}

	return [];
}

interface ClickStreamArgs {
	court: Court;
	duration: number;
	startDateTime?: number;
	slot?: Slot;
}

const Timeline = ({
	club,
	slots,
	timestamp,
	getReservationContext,
	clearReservationContext,
	reservation,
	showUnavailability,
	widgetMode,
}: TimelineProps) => {
	const [courtsOpen, setCourtsOpen] = useState(false);
	const { clubDetails, clubInfo } = club;
	const [click$, setClickStream] = useState(null as Subject<ClickStreamArgs>);

	const timezone = clubInfo.timeZone;

	const dayOfWeek = getDayOfWeek(timestamp, timezone);

	let openingHour =
		clubDetails.openingHours === null
			? null
			: (find(propEq("dayOfWeek", dayOfWeek))(clubDetails.openingHours) as OpeningHour);

	// Fires synchronously on every slot / duration click (preloadOnly), before the debounced context request
	const onSlotClick = (args: ClickStreamArgs & { preloadOnly?: boolean }) => {
		if (args.preloadOnly) {
			postWidgetMessage("slot_selected", {
				club_id: clubInfo.clubId,
				court_id: args.court?.courtId,
				// duration cells don't carry startDateTime; fall back to the current selection
				start_date_time: args.startDateTime ?? reservation.params?.startDateTime,
				duration: args.duration,
				logged_in: loggedIn(),
			});
		}

		return getReservationContext(args);
	};

	useLayoutEffect(() => {
		const click$ = new Subject<ClickStreamArgs>();

		const subscription = click$
			//.pipe(throttleTime(550, undefined, { leading: false, trailing: true }))
			.pipe(debounceTime(550))
			.subscribe(args => {
				if (loggedIn()) {
					getReservationContext(args);
				}
			});

		setClickStream(click$);

		return () => subscription.unsubscribe;
	}, []);

	useEffect(() => {
		// Check if there are slots at all
		if (!openingHour) {
			return;
		}
		// In today view, scroll to the current hour
		const today = moment();
		// Timestamp variable is without miliseconds and for some reason,
		// moment.js cannot compare timestamps with and without miliseconds
		const givenTimestamp = moment(timestamp * 1000);
		const isItToday = today.isSame(moment(givenTimestamp), "d");
		if (isItToday) {
			setTimeout(() => scroll(today, openingHour), 150);
		}
		// Solves bug with dragscroll plugin sometimes not active after page loads
		setTimeout(function() {
			dragscroll.reset();
		});
	}, [timestamp, openingHour]);

	const scroll = (today, openingHour: OpeningHour) => {
		const calendar = document.querySelector(".calendarScrollable");
		let x = (today.format("H") - openingHour.opening / 3600) * 80;
		if (!calendar) {
			return;
		}
		// Limit scroll to the left, so there is still most of the calendar in the view
		if (x + calendar.clientWidth >= calendar.scrollWidth - 320) {
			x = calendar.scrollWidth - calendar.clientWidth - 320;
		}
		calendar.scrollLeft = x;
	};

	if (!clubDetails.openingHours) {
		return <div className="noResults">{_("club.error.noOpeningHours")}</div>;
	}

	if (!openingHour) {
		return <div className="noResults">{_("club.error.noOpeningHour")}</div>;
	}

	const startOfDay = moment
		.unix(timestamp)
		.tz(timezone)
		.startOf("day");

	if (!slots) {
		return <Loader />;
	}

	const slotsByTime = orderSlotsByTime(slots);

	const timeline = [];
	//take care of day light savings by setting the absolute hour of opening and closing
	const openingComponents = moment
		.unix(openingHour.opening)
		.utc()
		.format("HH;mm;ss")
		.split(";");
	const closingComponents = moment
		.unix(openingHour.closing)
		.utc()
		.format("HH;mm;ss")
		.split(";");
	const startOfCalendar = startOfDay
		.clone()
		.hours(openingComponents[0])
		.minutes(0)
		.seconds(0); //we start the calendar at the full hour always
	const endOfCalendar = startOfDay
		.clone()
		.hours(closingComponents[0])
		.minutes(closingComponents[1])
		.seconds(closingComponents[2]);

	if (openingHour.closing > openingHour.opening && parseInt(closingComponents[0]) === 0) {
		//in this case closing is at midnight, add 1 day
		endOfCalendar.add(1, "day");
	}
	const opens = parseInt(openingComponents[0]);
	const calendarStartClone = startOfCalendar.clone().hours(opens - 1);

	while (endOfCalendar.diff(calendarStartClone) >= 0) {
		const hour = calendarStartClone.format("H");
		timeline.push(
			<div className="hour" key={calendarStartClone.format("D/HH")}>
				{hour}:00
				<div className="hourMarkdelta" />
				<div className="hourMark30" />
				<div className="hourMark45" />
			</div>
		);
		calendarStartClone.add(1, "hours");
	}

	timeline.push(
		<div className="hour" key={"padding1"}>
			<div className="hourMarkdelta" />
			<div className="hourMark30" />
			<div className="hourMark45" />
		</div>
	);
	timeline.push(
		<div className="hour" key={"padding2"}>
			<div className="hourMarkdelta" />
			<div className="hourMark30" />
			<div className="hourMark45" />
		</div>
	);
	timeline.push(
		<div className="hour" key={"padding3"}>
			<div className="hourMarkdelta" />
			<div className="hourMark30" />
			<div className="hourMark45" />
		</div>
	);
	timeline.push(
		<div className="hour" key={"padding4"}>
			<div className="hourMarkdelta" />
			<div className="hourMark30" />
			<div className="hourMark45" />
		</div>
	);

	// Group courts by sports
	let sports = {};
	slots.courts.forEach(function(court) {
		if (!sports[court.sport]) {
			sports[court.sport] = [];
		}
		sports[court.sport].push(court);
	});

	const courtElements = [];
	const slotsElements = [];
	const calendarBackground = [];

	for (let key in sports) {
		var sport = sports[key];

		courtElements.push(
			<div className="calendarSport" key={key}>
				<div className="calendarHeader">{_(key)}</div>
				<div
					className={cx("calendarCourts", { open: courtsOpen })}
					onClick={setCourtsOpen.bind(this, !courtsOpen)}
				>
					<div className="calendarCourtsIcons">
						{sport.map((court: Court) => (
							<div className="calendarCourtIcon" key={court.courtId}>
								<div
									className="courtIcon"
									data-court-sport={key}
									data-court-surface={court.surfaceType}
								/>
							</div>
						))}
					</div>
					<div className="calendarCourtsInformations">
						{sport.map((court: Court) => (
							<div className="calendarCourtInformation" key={court.courtId}>
								<div className="name">{court.name}</div>
								<div className="meta">
									{_("courtSurface." + court.surfaceType)} / {_("courtType." + court.courtType)}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		);

		slotsElements.push(
			<div className="calendarSlotsForSport" key={key}>
				{sport.map((court: Court) => {
					return (
						<div className="calendarRow" key={court.courtId}>
							{createSelectableRow(
								reservation.params.courtId === court.courtId,
								startOfDay,
								startOfCalendar,
								endOfCalendar,
								court,
								slotsByTime,
								getCourtSlots(slots, court.courtId),
								onSlotClick,
								reservation,
								court.bookingInterval,
								clearReservationContext,
								showUnavailability,
								timezone,
								click$
							)}
						</div>
					);
				})}
			</div>
		);

		calendarBackground.push(
			<div className="calendarSportBackground" key={key}>
				{sport.map((court: Court) => {
					return <div className="calendarRowBackground" key={court.courtId} />;
				})}
			</div>
		);
	}

	return (
		<div className="calendarWrapper">
			<div className="calendarFixed">
				<div className="calendarInfo">{courtElements}</div>
			</div>
			<div className="calendarScrollable dragscroll">
				<div className="calendarTimeline">{timeline}</div>
				<div className="calendarSlots">{slotsElements}</div>
			</div>
			<div className="calendarBackground">{calendarBackground}</div>
		</div>
	);
};

export default Timeline;
